import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  updateDoc,
  increment
} from 'firebase/firestore';

/**
 * Servicio de gestión de comentarios
 */

/**
 * Obtener comentarios de un post
 * @param {string} postId - ID del post
 * @param {number} limitCount - Número de comentarios a obtener
 * @returns {Promise<object>}
 */
export const getComments = async (postId, limitCount = 50) => {
  try {
    const commentsRef = collection(db, 'comments');
    const q = query(
      commentsRef,
      where('postId', '==', postId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const comments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      comments
    };
  } catch (error) {
    console.error('Error al obtener comentarios:', error);
    return {
      success: false,
      error: error.message,
      comments: []
    };
  }
};

/**
 * Agregar un comentario
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario
 * @param {string} userName - Nombre del usuario
 * @param {string} userPhotoURL - Foto del usuario
 * @param {string} text - Texto del comentario
 * @returns {Promise<object>}
 */
export const addComment = async (postId, userId, userName, userPhotoURL, text) => {
  try {
    // Crear el comentario
    const commentData = {
      postId,
      userId,
      userName,
      userPhotoURL,
      text,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'comments'), commentData);

    // Incrementar contador de comentarios en el post
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      commentsCount: increment(1)
    });

    // Notificación in-app al dueño del post (si no es comentario propio)
    const postSnap = await getDoc(postRef);
    const postOwnerId = postSnap.exists() ? postSnap.data().userId : null;
    if (postOwnerId && postOwnerId !== userId) {
      const { createNotification } = await import('./notificationService');
      await createNotification({
        userId: postOwnerId,
        type: 'comment',
        payload: {
          postId,
          commentId: docRef.id,
          actorUserId: userId,
          actorUserName: userName || 'Alguien',
          actorPhotoURL: userPhotoURL || null,
          textPreview: (text || '').slice(0, 50)
        }
      });
    }

    return {
      success: true,
      commentId: docRef.id,
      message: 'Comentario agregado'
    };
  } catch (error) {
    console.error('Error al agregar comentario:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eliminar un comentario
 * @param {string} commentId - ID del comentario
 * @param {string} postId - ID del post
 * @returns {Promise<object>}
 */
export const deleteComment = async (commentId, postId) => {
  try {
    // Eliminar el comentario
    await deleteDoc(doc(db, 'comments', commentId));

    // Decrementar contador de comentarios en el post
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      commentsCount: increment(-1)
    });

    return {
      success: true,
      message: 'Comentario eliminado'
    };
  } catch (error) {
    console.error('Error al eliminar comentario:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
